import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  addPartVideoMapping,
  getDiagramServiceSpecs,
  getPartDiagram,
  getPartDiagrams,
  getPartModels,
  getSubscriberAccess,
  removePartVideoMapping,
  searchHelpVideos,
} from '../parts/api';
import { getAdjacentDiagrams } from '../parts/diagramNavigation';
import ServiceSpecContributionModal from '../components/parts/ServiceSpecContributionModal';
import PelicanPartNumber, { usePelicanPartsPartnership } from '../components/parts/PelicanPartNumber';

const DEFAULT_MODEL_SLUG = 'porsche-boxster-986-1997-2004';
const LEGACY_MODEL_SLUGS = {
  '986-97-04': DEFAULT_MODEL_SLUG,
};
const PLACEHOLDER_IMAGE = '/assets/parts/diagram-placeholder.svg';

const VideoCard = ({ video, onRemove }) => (
  <div className="parts-video-card-wrap">
    <a
      className="parts-video-card"
      href={video.url}
      target="_blank"
      rel="noreferrer noopener"
      title={video.title}
      aria-label={`Watch Help Me DIY video: ${video.title}`}
    >
      {video.thumbnailUrl ? <img src={video.thumbnailUrl} alt="" /> : <span className="parts-video-placeholder">▶</span>}
      <div>
        <strong>{video.title}</strong>
        <span>Watch on YouTube</span>
      </div>
    </a>
    {onRemove && <button type="button" className="parts-admin-remove" onClick={() => onRemove(video)}>Remove</button>}
  </div>
);

const VideoMappingEditor = ({ label, modelSlug, sectionCode, part, onChanged }) => {
  const [query, setQuery] = useState('');
  const [videos, setVideos] = useState([]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);

  const target = part
    ? { partNumber: part.partNumber || '', position: part.position || '' }
    : { partNumber: '', position: '' };

  const runSearch = async () => {
    try {
      setBusy(true);
      setStatus('');
      const payload = await searchHelpVideos({ query, limit: 12 });
      setVideos(payload.videos || []);
    } catch (err) {
      setStatus(err.message || 'Video search failed.');
    } finally {
      setBusy(false);
    }
  };

  const addVideo = async (video) => {
    try {
      setBusy(true);
      await addPartVideoMapping({
        modelSlug,
        sectionCode,
        videoId: video.videoId,
        ...target,
        reason: `Manual admin mapping for ${label}`,
      });
      setStatus('Video mapped.');
      onChanged?.();
    } catch (err) {
      setStatus(err.message || 'Failed to map video.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="parts-admin-editor">
      <strong>Add video to {label}</strong>
      <div className="parts-admin-search-row">
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search Help Me DIY videos"
          onKeyDown={(event) => {
            if (event.key === 'Enter') runSearch();
          }}
        />
        <button type="button" onClick={runSearch} disabled={busy}>Search</button>
      </div>
      {status && <small className="parts-admin-status">{status}</small>}
      {videos.length > 0 && (
        <div className="parts-admin-video-results">
          {videos.map((video) => (
            <button type="button" key={video.videoId} onClick={() => addVideo(video)} disabled={busy} title={video.title}>
              + {video.title}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const modelLabel = (model, fallbackSlug) => {
  if (model?.label) return model.label;
  if (!model) return String(fallbackSlug || '').replace(/-/g, ' ');
  const code = model.modelCode || model.generation;
  const years = model.yearStart && model.yearEnd ? `${model.yearStart}–${model.yearEnd}` : '';
  return `${model.manufacturer} ${model.model}${code ? ` (${code})` : ''}${model.trim ? ` ${model.trim}` : ''}${years ? ` ${years}` : ''}`;
};

const normalizePartNumber = (value) =>
  String(value || '').replace(/[\s.\-_/]/g, '').toUpperCase();

const ServiceSpecCell = ({ mappings = [], onCorrect, onAddMissing }) => {
  if (!mappings.length) return (
    <div className="parts-service-list">
      <span className="parts-service-empty">No directly related spec</span>
      {onAddMissing && <button type="button" className="parts-service-contribute" onClick={onAddMissing}>+ Submit a missing spec</button>}
    </div>
  );
  return (
    <div className="parts-service-list">
      {mappings.map((mapping) => {
        const spec = mapping.specification;
        return (
          <div className="parts-service-spec" key={`${mapping.specKey}-${mapping.relation}`}>
            <strong>{spec.connection || spec.component}</strong>
            <div className="parts-service-values">
              {(spec.steps || []).map((step, index) => (
                <span key={`${step.kind}-${step.label}-${index}`}>
                  {step.label ? `${step.label}: ` : ''}{step.originalText}
                </span>
              ))}
            </div>
            {(spec.fastener?.size || spec.source?.pdfPage) && (
              <small>
                {spec.fastener?.size && `${spec.fastener.size} · `}
                Factory manual p.{spec.source.pdfPage}
              </small>
            )}
            {((spec.conditions || []).length > 0 || (spec.cautions || []).length > 0) && (
              <details>
                <summary>Conditions & cautions</summary>
                {(spec.conditions || []).map((item) => <div key={item}>{item}</div>)}
                {(spec.cautions || []).map((item) => <div className="parts-service-caution" key={item}>⚠ {item}</div>)}
              </details>
            )}
            {onCorrect && <button type="button" className="parts-service-contribute" onClick={() => onCorrect(mapping)}>Suggest a correction</button>}
          </div>
        );
      })}
    </div>
  );
};

const PartsDiagram = () => {
  const { modelSlug = DEFAULT_MODEL_SLUG, sectionCode } = useParams();
  const { user, isAdmin, isLoading: authLoading } = useAuth();
  const { pelicanPartsLinksEnabled, showCatalogPricing } = usePelicanPartsPartnership();
  const resolvedModelSlug = LEGACY_MODEL_SLUGS[modelSlug] || modelSlug || DEFAULT_MODEL_SLUG;
  const [diagram, setDiagram] = useState(null);
  const [modelDiagrams, setModelDiagrams] = useState([]);
  const [modelData, setModelData] = useState(null);
  const [parts, setParts] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [serviceAccess, setServiceAccess] = useState('loading');
  const [serviceMappings, setServiceMappings] = useState([]);
  const [contribution, setContribution] = useState(null);
  const [showContributionIntro, setShowContributionIntro] = useState(false);
  const [contributionStatus, setContributionStatus] = useState('');

  const reloadMappings = () => setRefreshKey((value) => value + 1);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const payload = await getPartDiagram({ modelSlug: resolvedModelSlug, sectionCode });
        setDiagram(payload.diagram);
        setParts(payload.parts || []);
      } catch (err) {
        setError(err.message || 'Failed to load diagram.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [resolvedModelSlug, sectionCode, refreshKey]);

  useEffect(() => {
    let active = true;
    Promise.all([
      getPartDiagrams({ modelSlug: resolvedModelSlug }),
      getPartModels(),
    ])
      .then(([diagramPayload, modelPayload]) => {
        if (!active) return;
        setModelDiagrams(diagramPayload.diagrams || []);
        setModelData((modelPayload.models || []).find((item) => item.slug === resolvedModelSlug) || null);
      })
      .catch(() => {
        if (!active) return;
        setModelDiagrams([]);
        setModelData(null);
      });
    return () => { active = false; };
  }, [resolvedModelSlug]);

  useEffect(() => {
    let active = true;
    const loadServiceSpecs = async () => {
      if (authLoading) return;
      setServiceMappings([]);
      if (!user) {
        setServiceAccess('visitor');
        return;
      }
      setServiceAccess('loading');
      try {
        const access = await getSubscriberAccess();
        if (!active) return;
        if (!access.eligible) {
          setServiceAccess('verification_required');
          return;
        }
        const payload = await getDiagramServiceSpecs({
          modelSlug: resolvedModelSlug,
          sectionCode,
        });
        if (!active) return;
        setServiceMappings(payload.mappings || []);
        setServiceAccess('verified');
      } catch (err) {
        if (!active) return;
        if (err.status === 403 || err.code === 'SUBSCRIBER_VERIFICATION_REQUIRED') {
          setServiceAccess('verification_required');
        } else {
          setServiceAccess('error');
        }
      }
    };
    loadServiceSpecs();
    return () => { active = false; };
  }, [user, authLoading, resolvedModelSlug, sectionCode]);

  useEffect(() => {
    if (serviceAccess !== 'verified' || !user?.id) return;
    const key = `helpmediy.serviceSpecs.contributionIntroSeen:${user.id}`;
    if (!window.localStorage.getItem(key)) setShowContributionIntro(true);
  }, [serviceAccess, user]);

  const dismissContributionIntro = () => {
    if (user?.id) window.localStorage.setItem(`helpmediy.serviceSpecs.contributionIntroSeen:${user.id}`, 'true');
    setShowContributionIntro(false);
  };

  const serviceMappingsForPart = (part) =>
    serviceMappings.filter((mapping) => {
      const mappedPartNumber = normalizePartNumber(mapping.partNumber);
      if (mappedPartNumber) return mappedPartNumber === normalizePartNumber(part.partNumber);
      return mapping.position && String(mapping.position) === String(part.position);
    });

  const removeMapping = async (video, part = null) => {
    try {
      await removePartVideoMapping({
        modelSlug: resolvedModelSlug,
        sectionCode,
        videoId: video.videoId,
        partNumber: part?.partNumber || '',
        position: part?.position || '',
      });
      if (part) {
        setParts((current) => current.map((row) => {
          const sameRow = row.partNumber === part.partNumber && row.position === part.position && row.sectionCode === part.sectionCode;
          if (!sameRow) return row;
          return { ...row, videos: (row.videos || []).filter((item) => item.videoId !== video.videoId) };
        }));
      } else {
        setDiagram((current) => current
          ? { ...current, videos: (current.videos || []).filter((item) => item.videoId !== video.videoId) }
          : current);
      }
    } catch (err) {
      setError(err.message || 'Failed to remove video mapping.');
    }
  };

  const filteredParts = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return parts;
    const normalized = q.replace(/[\s.\-_/]/g, '').toUpperCase();
    return parts.filter((part) => {
      const partNumber = String(part.partNumber || '');
      const normalizedPart = partNumber.replace(/[\s.\-_/]/g, '').toUpperCase();
      return (
        normalizedPart.includes(normalized) ||
        String(part.description || '').toLowerCase().includes(q) ||
        String(part.fitmentNotes || '').toLowerCase().includes(q) ||
        String(part.position || '').toLowerCase().includes(q)
      );
    });
  }, [parts, filter]);

  const relatedVideos = (diagram?.videos || [])
    .filter((video, index, all) => all.findIndex((item) => item.videoId === video.videoId) === index);
  const { previousDiagram, nextDiagram } = useMemo(
    () => getAdjacentDiagrams(modelDiagrams, sectionCode),
    [modelDiagrams, sectionCode]
  );
  const adjacentDiagramPath = (item) => item
    ? `/parts/porsche/${encodeURIComponent(resolvedModelSlug)}/${encodeURIComponent(item.sectionCode)}`
    : '';
  const handleAdjacentNavigation = () => window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  const diagramPath = `/parts/porsche/${encodeURIComponent(resolvedModelSlug)}/${encodeURIComponent(sectionCode)}`;
  const returnTo = encodeURIComponent(diagramPath);
  const serviceSpecsVisible = serviceAccess === 'verified';

  return (
    <div className="parts-page parts-detail-page">
      {error && <div className="error-banner">{error}</div>}
      {loading && <p>Loading diagram...</p>}
      {diagram && (
        <>
          <section className="parts-diagram-hero parts-diagram-navigation">
            <Link
              to="/parts"
              className="raffle-link-button secondary parts-diagram-back"
              aria-label="Back to parts search"
              title="Back to parts search"
            >
              <span aria-hidden="true">←</span> <span>Parts search</span>
            </Link>
            {previousDiagram ? (
              <Link
                className="parts-diagram-arrow parts-diagram-arrow-previous"
                to={adjacentDiagramPath(previousDiagram)}
                onClick={handleAdjacentNavigation}
                aria-label={`Previous diagram ${previousDiagram.sectionCode}`}
                title={`Previous diagram: ${previousDiagram.sectionCode}`}
              >
                <span aria-hidden="true">←</span>
                <strong>{previousDiagram.sectionCode}</strong>
              </Link>
            ) : (
              <span className="parts-diagram-arrow parts-diagram-arrow-disabled" aria-hidden="true">←</span>
            )}
            <div className="parts-diagram-heading">
              <span className="parts-diagram-model">{modelLabel(modelData, resolvedModelSlug)}</span>
              <span className="parts-chip">{diagram.category}</span>
              <h2>{diagram.sectionCode}</h2>
            </div>
            {nextDiagram ? (
              <Link
                className="parts-diagram-arrow parts-diagram-arrow-next"
                to={adjacentDiagramPath(nextDiagram)}
                onClick={handleAdjacentNavigation}
                aria-label={`Next diagram ${nextDiagram.sectionCode}`}
                title={`Next diagram: ${nextDiagram.sectionCode}`}
              >
                <strong>{nextDiagram.sectionCode}</strong>
                <span aria-hidden="true">→</span>
              </Link>
            ) : (
              <span className="parts-diagram-arrow parts-diagram-arrow-disabled" aria-hidden="true">→</span>
            )}
          </section>

          <section className="parts-diagram-layout">
            <div className="parts-diagram-image-card">
              {diagram.imageUrl ? (
                <img
                  src={diagram.imageUrl || PLACEHOLDER_IMAGE}
                  alt={`${diagram.sectionCode} ${diagram.title}`}
                  onError={(event) => {
                    event.currentTarget.src = PLACEHOLDER_IMAGE;
                  }}
                />
              ) : (
                <div className="parts-image-placeholder">Diagram image placeholder</div>
              )}
            </div>
            <aside className="parts-related-panel">
              <h3>Related Help Me DIY videos</h3>
              {relatedVideos.length === 0 && <p>No Help Me DIY video mapped yet for this diagram.</p>}
              {relatedVideos.map((video) => (
                <VideoCard
                  key={video.videoId}
                  video={video}
                  onRemove={isAdmin ? (item) => removeMapping(item) : null}
                />
              ))}
              {isAdmin && (
                <VideoMappingEditor
                  label="this diagram"
                  modelSlug={resolvedModelSlug}
                  sectionCode={sectionCode}
                  onChanged={reloadMappings}
                />
              )}
            </aside>
          </section>

          <section className="parts-table-section">
            <div className="parts-section-header">
              <h3>Parts in this diagram <span className="parts-count-pill">{filteredParts.length} of {parts.length}</span></h3>
              <input
                type="search"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                placeholder="Filter rows by part number, name, or position"
              />
            </div>
            {serviceAccess === 'visitor' && (
              <div className="parts-service-gate">
                <div className="parts-service-lock">🔒</div>
                <div>
                  <span>Subscriber benefit</span>
                  <strong>Unlock factory Service Specs</strong>
                  <p>Create a free account and verify your Help Me DIY YouTube subscription to see cited torque specs beside relevant parts.</p>
                </div>
                <div className="parts-service-actions">
                  <Link to={`/login?returnTo=${returnTo}`}>Sign in</Link>
                  <Link className="primary" to={`/login?mode=signup&returnTo=${returnTo}`}>Create account</Link>
                </div>
              </div>
            )}
            {serviceAccess === 'verification_required' && (
              <div className="parts-service-gate">
                <div className="parts-service-lock">▶</div>
                <div>
                  <span>Service Specs locked</span>
                  <strong>Verify your subscription to unlock Service Specs</strong>
                  <p>Confirm that you subscribe to Help Me DIY on YouTube to view the cited Service Specs for this diagram.</p>
                </div>
                <div className="parts-service-actions">
                  <Link className="primary" to={`/raffle/youtube?purpose=service-specs&returnTo=${returnTo}`}>Verify and unlock specs</Link>
                </div>
              </div>
            )}
            {serviceAccess === 'error' && (
              <div className="parts-service-gate error">
                <div className="parts-service-lock">!</div>
                <div><strong>Service Specs are temporarily unavailable</strong><p>Please try again shortly.</p></div>
              </div>
            )}
            {serviceSpecsVisible && (
              <div className="parts-service-verified">✓ <strong>Subscriber verified</strong> · Factory Service Specs are unlocked for this diagram.</div>
            )}
            {serviceSpecsVisible && showContributionIntro && (
              <div className="parts-service-community-intro">
                <div>
                  <span>Help improve Service Specs</span>
                  <strong>Your experience can help the whole community.</strong>
                  <p>If you spot an incorrect spec, suggest a correction. If a part has no spec, submit one with its source. Every contribution is reviewed by the Help Me DIY admin before publication.</p>
                </div>
                <button type="button" onClick={dismissContributionIntro}>Got it</button>
              </div>
            )}
            {contributionStatus && <div className="success-banner">{contributionStatus}</div>}
            <div className="parts-table-wrap" role="region" aria-label="Parts table — scroll horizontally for more columns" tabIndex={0}>
              <table className="parts-table">
                <thead>
                  <tr>
                    <th>Pos</th>
                    <th>Part Number</th>
                    <th>Description</th>
                    {serviceSpecsVisible && <th className="parts-service-column">Service Specs</th>}
                    <th>Qty</th>
                    <th>Notes</th>
                    <th>Help Me DIY Video</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredParts.length === 0 && (
                    <tr>
                      <td colSpan={serviceSpecsVisible ? 7 : 6} className="parts-empty-row">
                        {parts.length === 0
                          ? 'No parts are loaded for this diagram yet. Refresh the page or check the parts API connection.'
                          : 'No parts match the current filter.'}
                      </td>
                    </tr>
                  )}
                  {filteredParts.map((part, index) => (
                    <tr key={`${part.position}-${part.partNumber}-${index}`}>
                      <td>{part.position}</td>
                      <td><strong><PelicanPartNumber partNumber={part.partNumber} enabled={pelicanPartsLinksEnabled} /></strong></td>
                      <td>{part.description}</td>
                      {serviceSpecsVisible && (
                        <td className="parts-service-column">
                          <ServiceSpecCell
                            mappings={serviceMappingsForPart(part)}
                            onCorrect={(mapping) => setContribution({ part, mapping })}
                            onAddMissing={() => setContribution({ part, mapping: null })}
                          />
                        </td>
                      )}
                      <td>{part.quantity || '—'}</td>
                      <td>{part.fitmentNotes || (showCatalogPricing ? part.priceText : '') || '—'}</td>
                      <td>
                        {(part.videos || []).length ? (
                          <div className="parts-video-pills">
                            {part.videos.map((video) => (
                              <span className="parts-video-pill-admin" key={video.videoId}>
                                <a
                                  href={video.url}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  title={video.title}
                                  aria-label={`View video: ${video.title}`}
                                >
                                  View Video
                                </a>
                                {isAdmin && <button type="button" onClick={() => removeMapping(video, part)}>×</button>}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="parts-no-video">No video mapped yet</span>
                        )}
                        {isAdmin && (
                          <VideoMappingEditor
                            label={`part ${part.partNumber || part.position}`}
                            modelSlug={resolvedModelSlug}
                            sectionCode={sectionCode}
                            part={part}
                            onChanged={reloadMappings}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {serviceSpecsVisible && (
              <p className="parts-service-disclaimer">Service values include factory source citations. Always confirm model-year applicability and conditions before use.</p>
            )}
          </section>
        </>
      )}
      {contribution && (
        <ServiceSpecContributionModal
          modelSlug={resolvedModelSlug}
          sectionCode={sectionCode}
          part={contribution.part}
          mapping={contribution.mapping}
          onClose={() => setContribution(null)}
          onSubmitted={() => {
            setContributionStatus('Thank you for helping the community. Your submission is pending admin review.');
            setTimeout(() => setContribution(null), 900);
          }}
        />
      )}
    </div>
  );
};

export default PartsDiagram;
